import { useEffect, useRef, useState } from "react";

import { previewTournamentSetup } from "@/libs/tournamentSetupApi";
import type { SetupPreview, SetupPreviewRequest } from "@/libs/tournamentSetupApi";

/** The server preview for a setup draft: 300 ms debounce, keeps the previous
 * result while a newer one loads, ignores stale responses, and clears when the
 * draft becomes incomplete. */
export function useSetupPreview(facilityId: string | null, draft: SetupPreviewRequest | null) {
  const [data, setData] = useState<SetupPreview | undefined>(undefined);
  const [isFetching, setFetching] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const sequence = useRef(0);
  const key = draft ? JSON.stringify(draft) : "";
  useEffect(() => {
    if (!facilityId || !draft) {
      // Nothing to preview: drop the last result so nothing can act on it.
      sequence.current += 1;
      setData(undefined);
      setError(undefined);
      setFetching(false);
      return;
    }
    const ticket = ++sequence.current;
    setFetching(true);
    const timer = window.setTimeout(() => {
      previewTournamentSetup(facilityId, draft)
        .then((result) => {
          if (ticket === sequence.current) {
            setData(result);
            setError(undefined);
          }
        })
        .catch((cause: unknown) => {
          if (ticket === sequence.current) setError(cause);
        })
        .finally(() => {
          if (ticket === sequence.current) setFetching(false);
        });
    }, 300);
    return () => window.clearTimeout(timer);
    // `key` captures the draft's content; the object identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, key]);
  return { data, isFetching, error };
}
